import type { MutableRefObject } from 'react'
import { useEffect } from 'react'
import { isRef } from '../react-utils'
import { useRafState } from './useRafState'

export interface State {
  x: number
  y: number
}

export interface UseElementScrollOptions<T extends HTMLElement> {
  enabled?: boolean
  target?: Window | T | MutableRefObject<T>
}

function isWindow<T extends HTMLElement>(target: Window | T): target is Window {
  return target === window
}

function getScrollPosition<T extends HTMLElement>(target: Window | T): State {
  return {
    x: isWindow<T>(target) ? target.scrollX : target?.scrollLeft ?? 0,
    y: isWindow<T>(target) ? target.scrollY : target?.scrollTop ?? 0
  }
}

export function useElementScroll<T extends HTMLElement>(
  options?: UseElementScrollOptions<T>
): State {
  const { enabled = true, target: targetProp = window } = options || {}
  const target = isRef(targetProp) ? targetProp.current : targetProp
  const [state, setState] = useRafState<State>(getScrollPosition<T>(target))

  useEffect(() => {
    if (!enabled) return

    const handler = () => {
      setState(state => {
        const { x: scrollX, y: scrollY } = getScrollPosition<T>(target)
        return state.x !== scrollX || state.y !== scrollY
          ? {
              x: scrollX,
              y: scrollY
            }
          : state
      })
    }

    handler()

    target.addEventListener('scroll', handler)

    return () => {
      target.removeEventListener('scroll', handler)
    }
  }, [enabled])

  return state
}
